import type { ApiQuery, ApiResponse, MutationResult } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import type { AdminUsersRepository } from './repository';
import type { AdminUserListQuery, PermissionProfileListQuery } from './types';

export function createRealAdminUsersRepository(
  apiClient: ApiClient,
  accessToken: string | null,
): AdminUsersRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async listUsers(query, signal) {
      const result = await apiClient.get<ApiResponse<'/api/v1/admin-users', 'get'>>(
        '/api/v1/admin-users',
        { ...auth, query: userQuery(query), signal },
      );
      return page(result);
    },
    async getUser(userId, signal) {
      const result = await apiClient.get<ApiResponse<'/api/v1/admin-users/{id}', 'get'>>(
        userPath(userId),
        { ...auth, signal },
      );
      return result.data.data;
    },
    async inviteUser(input) {
      return mutate(
        await apiClient.post<ApiResponse<'/api/v1/admin-users', 'post'>>(
          '/api/v1/admin-users',
          input,
          auth,
        ),
      );
    },
    async updateUser(userId, input) {
      return mutate(
        await apiClient.patch<ApiResponse<'/api/v1/admin-users/{id}', 'patch'>>(
          userPath(userId),
          input,
          auth,
        ),
      );
    },
    async changeAccess(userId, input) {
      return mutate(
        await apiClient.post<ApiResponse<'/api/v1/admin-users/{id}/access', 'post'>>(
          `${userPath(userId)}/access`,
          input,
          auth,
        ),
      );
    },
    async changeStatus(userId, input) {
      return mutate(
        await apiClient.post<ApiResponse<'/api/v1/admin-users/{id}/status', 'post'>>(
          `${userPath(userId)}/status`,
          input,
          auth,
        ),
      );
    },
    async listPermissions(signal) {
      const result = await apiClient.get<ApiResponse<'/api/v1/permissions', 'get'>>(
        '/api/v1/permissions',
        { ...auth, signal },
      );
      return result.data.data;
    },
    async listProfiles(query, signal) {
      const result = await apiClient.get<ApiResponse<'/api/v1/permission-profiles', 'get'>>(
        '/api/v1/permission-profiles',
        { ...auth, query: profileQuery(query), signal },
      );
      return page(result);
    },
    async getProfile(profileId, signal) {
      const result = await apiClient.get<ApiResponse<'/api/v1/permission-profiles/{id}', 'get'>>(
        profilePath(profileId),
        { ...auth, signal },
      );
      return result.data.data;
    },
    async createProfile(input) {
      return mutate(
        await apiClient.post<ApiResponse<'/api/v1/permission-profiles', 'post'>>(
          '/api/v1/permission-profiles',
          input,
          auth,
        ),
      );
    },
    async updateProfile(profileId, input) {
      return mutate(
        await apiClient.patch<ApiResponse<'/api/v1/permission-profiles/{id}', 'patch'>>(
          profilePath(profileId),
          input,
          auth,
        ),
      );
    },
    async changeProfileStatus(profileId, input) {
      return mutate(
        await apiClient.post<ApiResponse<'/api/v1/permission-profiles/{id}/status', 'post'>>(
          `${profilePath(profileId)}/status`,
          input,
          auth,
        ),
      );
    },
  };
}

function userQuery(query: AdminUserListQuery): ApiQuery<'/api/v1/admin-users', 'get'> {
  return {
    page: query.page,
    pageSize: query.pageSize,
    sort: query.sort,
    order: query.order,
    ...(query.filters.role === '' ? {} : { role: query.filters.role }),
    ...(query.filters.status === '' ? {} : { status: query.filters.status }),
    ...(query.filters.partnerId === '' ? {} : { partnerId: query.filters.partnerId }),
    ...(query.filters.agentId === '' ? {} : { agentId: query.filters.agentId }),
    ...(query.filters.permissionProfileId === ''
      ? {}
      : { permissionProfileId: query.filters.permissionProfileId }),
    ...(query.filters.username === '' ? {} : { username: query.filters.username }),
    ...(query.filters.email === '' ? {} : { email: query.filters.email }),
  };
}

function profileQuery(
  query: PermissionProfileListQuery,
): ApiQuery<'/api/v1/permission-profiles', 'get'> {
  return {
    page: query.page,
    pageSize: query.pageSize,
    sort: query.sort,
    order: query.order,
    ...(query.filters.partnerId === '' ? {} : { partnerId: query.filters.partnerId }),
    ...(query.filters.agentId === '' ? {} : { agentId: query.filters.agentId }),
    ...(query.filters.status === '' ? {} : { status: query.filters.status }),
    ...(query.filters.name === '' ? {} : { name: query.filters.name }),
  };
}

function page<T>(result: {
  readonly data: { readonly data: readonly T[]; readonly pagination: { readonly total: number } };
  readonly requestId: string | null;
}) {
  return {
    items: result.data.data,
    total: result.data.pagination.total,
    requestId: result.requestId,
  };
}

function mutate<T>(result: {
  readonly data: { readonly data: T };
  readonly requestId: string | null;
}): MutationResult<T> {
  return { data: result.data.data, requestId: result.requestId };
}

function userPath(userId: string): string {
  return `/api/v1/admin-users/${encodeURIComponent(userId)}`;
}

function profilePath(profileId: string): string {
  return `/api/v1/permission-profiles/${encodeURIComponent(profileId)}`;
}
